import { supertypeClass, property, Persistable, Remoteable, Supertype } from '@haventech/amorphic';
import { IsomorphicPersistent } from '@hit/amorphic-types';
import { Customer } from './Customer';
import { ResidentialAsset } from './ResidentialAsset';

@supertypeClass({ toClient: true, toServer: true })
export class PolicyBusinessPurpose extends Persistable(Remoteable(Supertype)) {
  @property({
    rule: 'required',
    comment: 'Key Employee (purpose for buying this policy)',
  })
  isKeyEmployee: boolean;

  @property({
    rule: 'required',
    comment: 'Buy Or Sell (purpose for buying this policy)',
  })
  isBuyOrSell: boolean;

  @property({
    rule: 'required',
    comment: 'Loan Guarantee Coverage (purpose for buying this policy)',
  })
  hasLoanGuaranteeCoverage: boolean;

  @property({
    rule: 'required',
    comment: 'Deferred Compensation (purpose for buying this policy)',
  })
  hasDeferredCompensation: boolean;

  @property({
    rule: 'required',
    comment: 'Split Dollar (purpose for buying this policy)',
  })
  isSplitDollar: boolean;

  @property({
    rule: 'required',
    comment: 'Target Benefit Plan (purpose for buying this policy)',
  })
  isTargetBenefitPlan: boolean;

  @property({
    rule: 'required',
    comment: 'Other (purpose for buying this policy)',
  })
  hasOther: boolean;

  @property({
    rule: 'required',
    comment: 'Details about the main purpose in buying this policy',
  })
  reasonForBuyingPolicyDetails: string;

  @property({
    rule: 'required',
    comment: 'Business Name',
  })
  name: string;

  @property({
    rule: 'required',
    comment: 'Business Type',
  })
  type: string;

  @property({
    rule: ['numeric', 'required', 'validPastYear'],
    comment: 'Year established',
  })
  yearEstablished: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Estimated net value',
  })
  estimatedNetValue: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Number of employees',
  })
  numberOfEmployees: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Percentage of company you own',
  })
  percentageOwned: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Total Assets',
  })
  totalAssets: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Total Liabilities',
  })
  totalLiabilities: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Total Net Worth',
  })
  totalNetWorth: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Market Value',
  })
  marketValue: number;

  @property({
    rule: ['date', 'pastDate', 'required'],
    comment: 'Date of last formal valuation',
  })
  dateLastFormalValuation: Date;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Current Year',
  })
  netProfitBeforeTaxesCurrentYear: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Previous Year',
  })
  netProfitBeforeTaxesPrevYear: number;

  @property({
    rule: 'required',
    comment: 'Does the business carry life insurance on the lives of any partners or business officers?',
  })
  carryLifeInsurance: string;

  @property({
    rule: 'required',
    comment: 'Please provide details',
  })
  carryLifeInsuranceDetails: string;

  @property({
    getType: () => BusinessPurposePartners,
    comment: 'List of policy business purpose partners',
  })
  businessPurposePartners: Array<BusinessPurposePartners> = [];

  @property({
    rule: 'required',
    comment: 'Special Skills, Knowledge or Abilities',
  })
  employeeSpecialSkills: string;

  @property({
    rule: 'required',
    comment: 'Is there a written Buy/Sell or Stock Redemption agreement?',
  })
  writtenAgreement: string;

  @property({
    rule: ['date', 'pastDate', 'required'],
    comment: 'When was the agreement originated',
  })
  agreementOriginationDate: Date;

  @property({
    rule: 'required',
    comment: 'Details (Provide additional details indicating question)',
  })
  employeeAdditionalDetails: string;

  constructor() {
    super();
  }

  static new() {
    return new PolicyBusinessPurpose().init();
  }

  init() {
    return this;
  }

  carryLifeInsuranceTrigger() {
    if (this.carryLifeInsurance === 'Yes') {
      this.businessPurposePartners.push(BusinessPurposePartners.new(null, this));
    } else {
      this.businessPurposePartners = [];
    }
  }
}

@supertypeClass({ toClient: true, toServer: true })
export class BusinessPurposePartners extends IsomorphicPersistent {
  @property({
    rule: 'required',
    comment: 'Full name of whom the business carries life insurance for',
  })
  carryLifeInsuranceName: string;

  @property({
    rule: 'required',
    comment: 'Title of whom the business carries life insurance for',
  })
  carryLifeInsuranceTitle: string;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Face amount of the life insurance that the business carries',
  })
  carryLifeInsuranceFaceAmount: number;

  @property({
    rule: ['numeric', 'required'],
    comment: 'Percentage owned of the life insurance that the business carries',
  })
  carryLifeInsurancePercentOwned: number;

  @property({
    comment: 'parent property - policy business purpose',
  })
  policyBusinessPurpose: PolicyBusinessPurpose;

  constructor() {
    super();
  }

  static new(customer: Customer, policyBusinessPurpose?: PolicyBusinessPurpose) {
    return new BusinessPurposePartners().init(customer, policyBusinessPurpose);
  }

  init(customer: Customer, policyBusinessPurpose?: PolicyBusinessPurpose) {
    this.policyBusinessPurpose = policyBusinessPurpose || customer.application.primaryPolicy.policyBusinessPurpose;
    return this;
  }
}
